import React, { useMemo, useState } from 'react';
import { Plus, Save, Trash2 } from 'lucide-react';
import {
  centsToDollarsInput,
  dollarsToCents,
  MAX_REM_COST_CENTS,
  findBandProblems,
  type RemBand,
  type RemBrokerCost,
} from '../../../hooks/pipelineTracker/remContract';
import type { RemSaveOutcome } from '../../../hooks/pipelineTracker/useRemSettings';
import { money } from './pipelineParts';

/**
 * The admin-only editor for the settlement bands and each broker's monthly costs.
 *
 * BANDS ARE EDITED BY THEIR UPPER BOUND. Each band starts where the one before it ends and the
 * first starts at $0, so the operator types only where each band stops (and ticks "and above" on
 * the last). That makes a gap or an overlap impossible to type rather than something to catch
 * after the fact, which is the scheme Matt Spears described: a month's volume always lands in
 * exactly one band. The server re-checks the same rules and is the gate.
 *
 * Amounts are typed in dollars and converted to integer cents from the string, never by
 * multiplying a float, so "0.29" is 29 cents.
 */

interface BandDraft {
  to: string;
  open: boolean;
  rate: string;
}

interface CostDraft {
  base: string;
  support: string;
}

function draftFromBands(bands: readonly RemBand[]): BandDraft[] {
  return bands.map((band) => ({
    to: band.toCents === null ? '' : centsToDollarsInput(band.toCents),
    open: band.toCents === null,
    rate: String(band.ratePct),
  }));
}

/** Drafts to bands, or the first reason they cannot be. Pure, exported for tests. */
export function bandsFromDraft(drafts: readonly BandDraft[]): { bands: RemBand[] } | { error: string } {
  const bands: RemBand[] = [];
  let from = 0;
  for (let i = 0; i < drafts.length; i += 1) {
    const d = drafts[i];
    const rate = Number(d.rate);
    if (d.rate.trim() === '' || !Number.isFinite(rate)) return { error: `Band ${i + 1} needs a rate.` };
    let toCents: number | null = null;
    if (!d.open) {
      toCents = dollarsToCents(d.to);
      if (toCents === null) return { error: `Band ${i + 1} needs an upper amount in dollars.` };
    }
    bands.push({ fromCents: from, toCents, ratePct: rate });
    if (toCents === null) {
      if (i < drafts.length - 1) return { error: `Only the last band can be "and above".` };
    } else {
      from = toCents;
    }
  }
  const problems = findBandProblems(bands);
  if (problems.length > 0) return { error: `${problems[0].message}.` };
  return { bands };
}

export const RemSettingsEditor: React.FC<{
  bands: readonly RemBand[];
  brokerCosts: Readonly<Record<string, RemBrokerCost>>;
  /** Everyone the report knows about, so costs can be entered for people with no saved row yet. */
  people: ReadonlyArray<{ adviserKey: string; adviserName: string }>;
  saving: boolean;
  onSave: (bands: RemBand[], brokerCosts: Record<string, RemBrokerCost>) => Promise<RemSaveOutcome>;
  onClose: () => void;
}> = ({ bands, brokerCosts, people, saving, onSave, onClose }) => {
  const [drafts, setDrafts] = useState<BandDraft[]>(() => draftFromBands(bands));
  const rows = useMemo(() => {
    const byKey = new Map(people.map((p) => [p.adviserKey, p.adviserName]));
    // A saved cost for someone absent from this snapshot is kept, not silently dropped.
    for (const key of Object.keys(brokerCosts)) if (!byKey.has(key)) byKey.set(key, key);
    return [...byKey.entries()]
      .filter(([key]) => key !== '')
      .map(([adviserKey, adviserName]) => ({ adviserKey, adviserName }))
      .sort((a, b) => a.adviserName.localeCompare(b.adviserName));
  }, [people, brokerCosts]);
  const [costs, setCosts] = useState<Record<string, CostDraft>>(() =>
    Object.fromEntries(
      rows.map((r) => [r.adviserKey, {
        base: centsToDollarsInput(brokerCosts[r.adviserKey]?.monthlyBaseCents),
        support: centsToDollarsInput(brokerCosts[r.adviserKey]?.monthlySupportCents),
      }])
    )
  );
  const [message, setMessage] = useState<{ tone: 'good' | 'bad'; text: string } | null>(null);

  const built = bandsFromDraft(drafts);

  const setDraft = (i: number, patch: Partial<BandDraft>) =>
    setDrafts((prev) => prev.map((d, j) => (j === i ? { ...d, ...patch } : d)));

  const submit = async () => {
    setMessage(null);
    if ('error' in built) { setMessage({ tone: 'bad', text: built.error }); return; }
    const out: Record<string, RemBrokerCost> = {};
    for (const [key, draft] of Object.entries(costs)) {
      const entry: RemBrokerCost = {};
      for (const [field, text] of [['monthlyBaseCents', draft.base], ['monthlySupportCents', draft.support]] as const) {
        if (text.trim() === '') continue;
        const cents = dollarsToCents(text);
        if (cents === null) { setMessage({ tone: 'bad', text: 'Costs must be dollar amounts of $0 or more.' }); return; }
        if (cents > MAX_REM_COST_CENTS) {
          setMessage({ tone: 'bad', text: `A monthly cost cannot exceed $${(MAX_REM_COST_CENTS / 100).toLocaleString('en-AU')}.` });
          return;
        }
        entry[field] = cents;
      }
      if (entry.monthlyBaseCents !== undefined || entry.monthlySupportCents !== undefined) out[key] = entry;
    }
    const result = await onSave(built.bands, out);
    setMessage(result.ok ? { tone: 'good', text: 'Saved.' } : { tone: 'bad', text: result.message });
  };

  const input = 'w-28 rounded-md border border-gray-700 bg-gray-900/70 px-2 py-1 text-right text-sm tabular-nums text-gray-100';

  return (
    <div className="space-y-5 rounded-lg border border-gray-700/70 bg-gray-900/40 p-4" aria-label="Remuneration settings">
      <div>
        <h4 className="text-sm font-semibold text-gray-100">Settlement volume bands</h4>
        <p className="mt-0.5 text-xs text-gray-400">
          Each band starts where the one before it ends. The rate is the broker&apos;s split of their own
          net commission for the month, applied to the whole month.
        </p>
        <table className="mt-3 text-sm">
          <thead>
            <tr className="text-left text-[0.6875rem] uppercase tracking-wide text-gray-400">
              <th className="pb-1.5 pr-3 font-semibold">Band</th>
              <th className="pb-1.5 pr-3 font-semibold">From</th>
              <th className="pb-1.5 pr-3 font-semibold">Up to ($)</th>
              <th className="pb-1.5 pr-3 font-semibold">Rate (%)</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {drafts.map((d, i) => {
              const from = i === 0 ? 0 : dollarsToCents(drafts[i - 1].to);
              return (
                <tr key={i}>
                  <td className="py-1 pr-3 text-gray-300">{i + 1}</td>
                  <td className="py-1 pr-3 tabular-nums text-gray-400">{from === null ? '-' : money(from)}</td>
                  <td className="py-1 pr-3">
                    {d.open ? (
                      <span className="text-gray-400">and above</span>
                    ) : (
                      <input aria-label={`Band ${i + 1} up to`} className={input} value={d.to}
                        onChange={(e) => setDraft(i, { to: e.target.value })} inputMode="decimal" />
                    )}
                    {i === drafts.length - 1 && (
                      <label className="ml-2 inline-flex items-center gap-1 text-xs text-gray-400">
                        <input type="checkbox" checked={d.open} onChange={(e) => setDraft(i, { open: e.target.checked })} />
                        and above
                      </label>
                    )}
                  </td>
                  <td className="py-1 pr-3">
                    <input aria-label={`Band ${i + 1} rate`} className={input} value={d.rate}
                      onChange={(e) => setDraft(i, { rate: e.target.value })} inputMode="decimal" />
                  </td>
                  <td className="py-1">
                    <button type="button" aria-label={`Remove band ${i + 1}`}
                      onClick={() => setDrafts((prev) => prev.filter((_, j) => j !== i))}
                      className="rounded p-1 text-gray-500 hover:text-red-300">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <button type="button"
          onClick={() => setDrafts((prev) => [
            ...prev.map((d, j) => (j === prev.length - 1 && d.open ? { ...d, open: false } : d)),
            { to: '', open: true, rate: '' },
          ])}
          className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-gray-700 px-2.5 py-1 text-xs font-semibold text-gray-300 hover:bg-white/[0.05]">
          <Plus className="h-3.5 w-3.5" /> Add band
        </button>
        {'error' in built && drafts.length > 0 && (
          <p className="mt-2 text-xs text-amber-300" role="status">{built.error}</p>
        )}
      </div>

      <div>
        <h4 className="text-sm font-semibold text-gray-100">Monthly broker costs</h4>
        <p className="mt-0.5 text-xs text-gray-400">
          The practice&apos;s own base and support cost per broker, per month. Leave blank if not known; blank is
          shown as not entered, never as $0.
        </p>
        {rows.length === 0 ? (
          <p className="mt-2 text-xs text-gray-400">No brokers in the snapshot yet.</p>
        ) : (
          <table className="mt-3 text-sm">
            <thead>
              <tr className="text-left text-[0.6875rem] uppercase tracking-wide text-gray-400">
                <th className="pb-1.5 pr-3 font-semibold">Broker</th>
                <th className="pb-1.5 pr-3 font-semibold">Base ($ / month)</th>
                <th className="pb-1.5 font-semibold">Support ($ / month)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.adviserKey}>
                  <td className="py-1 pr-3 text-gray-200">{r.adviserName}</td>
                  {(['base', 'support'] as const).map((field) => (
                    <td key={field} className="py-1 pr-3">
                      <input aria-label={`${r.adviserName} ${field}`} className={input} inputMode="decimal"
                        value={costs[r.adviserKey]?.[field] ?? ''}
                        onChange={(e) => setCosts((prev) => ({
                          ...prev,
                          [r.adviserKey]: { ...(prev[r.adviserKey] ?? { base: '', support: '' }), [field]: e.target.value },
                        }))} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => void submit()} disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60">
          <Save className="h-4 w-4" /> {saving ? 'Saving' : 'Save settings'}
        </button>
        <button type="button" onClick={onClose} className="text-sm text-gray-400 hover:text-gray-200">Close</button>
        {message && (
          <span role="status" className={`text-xs ${message.tone === 'good' ? 'text-green-300' : 'text-amber-300'}`}>{message.text}</span>
        )}
      </div>
    </div>
  );
};

export default RemSettingsEditor;
